/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import React, { useRef, useState } from 'react';

import { Colors } from 'src/common/ui/base/Colors';

export type ExpandCollapseSectionProps = {
  label: string;
  children: React.ReactNode;
  defaultExpanded?: boolean;
};

// React 17 here has no useId, so we mint a process-stable unique suffix per mounted instance.
// Captured once in a ref so the id is stable across re-renders (not regenerated each render),
// which keeps the header's aria-controls pointing at the same body element.
let nextId = 0;

const headerBase = css`
  background: ${Colors.White};
  border: 1px solid ${Colors.CoolGray2};
  box-sizing: border-box;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  width: 100%;

  &:hover {
    background: rgba(0, 0, 0, 0.04);
  }
`;

export const ExpandCollapseSection = ({
  label,
  children,
  defaultExpanded = false,
}: ExpandCollapseSectionProps) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const bodyId = useRef(`expand-collapse-section-${(nextId += 1)}`).current;

  return (
    <div
      css={css`
        width: 100%;
        box-sizing: border-box;
      `}
    >
      <button
        type="button"
        aria-expanded={expanded}
        aria-controls={bodyId}
        onClick={() => setExpanded(prev => !prev)}
        css={css`
          ${headerBase};
          border-radius: ${expanded ? '12px 12px 0 0' : '12px'};
          ${expanded && 'border-bottom: none;'}
        `}
      >
        <span
          css={css`
            font-family: 'Nunito', 'Nunito Sans';
            font-size: 14px;
            font-weight: 700;
            letter-spacing: 0.12em;
            text-transform: uppercase;
            color: ${Colors.CoolGray9};
          `}
        >
          {label}
        </span>
        {expanded ? <ExpandLessIcon css={chevronStyle} /> : <ExpandMoreIcon css={chevronStyle} />}
      </button>

      {expanded && (
        <div
          id={bodyId}
          css={css`
            border: 1px solid ${Colors.CoolGray2};
            border-top: none;
            border-radius: 0 0 12px 12px;
            box-sizing: border-box;
            display: flex;
            flex-direction: column;
            gap: 8px;
            padding: 8px 0;
            width: 100%;
          `}
        >
          {children}
        </div>
      )}
    </div>
  );
};

const chevronStyle = css`
  color: ${Colors.CoolGray9};
  width: 32px;
  height: 32px;
  flex-shrink: 0;
`;
